import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  ActivityIndicator,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { router, useLocalSearchParams } from "expo-router";
import { Toast } from "react-native-toast-notifications";
import { useDispatch, useSelector } from "react-redux";
import AsyncStorage from "@react-native-async-storage/async-storage";
import i18n, { availableLocales } from "../../locales/i18n";
import customAxios from "../../utils/customAxios";
import secureStorage from "../../utils/secureStorage";
import { authActions } from "../../store/slices/authSlice";
import { appActions } from "../../store/slices/appSlice";
import { RootState } from "../../store/index";

// Import images
import VerifyImage from "../../assets/images/stock.png";

const WebVerifyEmailScreen: React.FC = () => {
  const locale = i18n.locale;
  const dispatch = useDispatch();
  const { email: paramEmail, type } = useLocalSearchParams();

  const { user, pendingVerificationEmail } = useSelector(
    (state: RootState) => state.auth
  );

  const [verificationCode, setVerificationCode] = useState("");
  const [buttonSpinner, setButtonSpinner] = useState(false);
  const [resendSpinner, setResendSpinner] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [initialCodeSent, setInitialCodeSent] = useState(false);
  const [verificationType, setVerificationType] = useState<string | null>(null);
  const [showLanguages, setShowLanguages] = useState(false);

  // Get email from various sources on component mount
  useEffect(() => {
    const loadUserEmail = async () => {
      try {
        let email = null;

        if (pendingVerificationEmail) {
          email = pendingVerificationEmail;
        } else if (paramEmail) {
          email = paramEmail as string;
          dispatch(authActions.setPendingVerificationEmail(email));
        } else {
          const stored = await AsyncStorage.getItem("pendingVerificationEmail");
          if (stored) {
            email = stored;
            dispatch(authActions.setPendingVerificationEmail(email));
          } else if (user?.email) {
            email = user.email;
            dispatch(authActions.setPendingVerificationEmail(email));
          }
        }

        setUserEmail(email);

        if (type) {
          setVerificationType(type as string);
        }
      } catch (error) {
        // silently ignore
      }
    };

    loadUserEmail();
  }, [paramEmail, user, pendingVerificationEmail, dispatch, type]);

  // Automatically send verification code when email is loaded
  useEffect(() => {
    const sendInitialCode = async () => {
      if (
        userEmail &&
        !initialCodeSent &&
        verificationType !== "password-reset"
      ) {
        setInitialCodeSent(true);
        try {
          const response = await customAxios.post(
            `/users/resend-verification`,
            {
              email: userEmail,
            }
          );

          Toast.show(response.data.message || i18n.t("verificationCodeSent"), {
            type: "success",
            duration: 4000,
          });
        } catch (error: any) {
          // silently ignore
        }
      }
    };

    sendInitialCode();
  }, [userEmail, initialCodeSent, verificationType]);

  const handleVerifyEmail = async () => {
    if (!verificationCode || verificationCode.trim() === "") {
      Toast.show(i18n.t("enterVerificationCode"), { type: "danger" });
      return;
    }

    setButtonSpinner(true);
    try {
      if (!userEmail) {
        Toast.show(i18n.t("emailRequired"), { type: "danger" });
        setButtonSpinner(false);
        return;
      }

      const response = await customAxios.post(`/users/verify-email`, {
        email: userEmail,
        verificationCode: verificationCode.trim(),
      });

      if (response.data.accessToken && response.data.refreshToken) {
        await AsyncStorage.setItem("accessToken", response.data.accessToken);
        await secureStorage.setItemAsync(
          "refreshToken",
          response.data.refreshToken
        );

        try {
          const userDetailsResponse = await customAxios.get("/users/check");
          dispatch(authActions.setUser(userDetailsResponse.data?.userinfo));
        } catch (userError) {
          const userData = {
            id: response.data?.userinfo?.id,
            username: response.data?.userinfo?.username || response.data?.user,
            email: userEmail,
            payed: response.data?.payed || false,
            verified: true,
          };
          dispatch(authActions.setUser(userData));
        }
      }

      dispatch(authActions.clearPendingVerificationEmail());
      await AsyncStorage.removeItem("pendingVerificationEmail");

      const successMessage =
        verificationType === "password-reset"
          ? i18n.t("passwordResetSuccess")
          : i18n.t("emailVerified");

      Toast.show(response.data.message || successMessage, { type: "success" });
      setButtonSpinner(false);
      router.replace("/(drawer)/start");
    } catch (error: any) {
      setButtonSpinner(false);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t("emailVerificationFailed");

      Toast.show(errorMessage, { type: "danger" });
    }
  };

  const handleResendCode = async () => {
    setResendSpinner(true);
    try {
      if (!userEmail) {
        Toast.show(i18n.t("emailRequired"), { type: "danger" });
        setResendSpinner(false);
        return;
      }

      let endpoint, requestBody;
      if (verificationType === "password-reset") {
        endpoint = `/users/forgot-password/`;
        requestBody = { email: userEmail };
      } else {
        endpoint = `/users/resend-verification`;
        requestBody = { email: userEmail };
      }

      const response = await customAxios.post(endpoint, requestBody);

      Toast.show(response.data.message || i18n.t("verificationCodeResent"), {
        type: "success",
      });
      setResendSpinner(false);
    } catch (error: any) {
      setResendSpinner(false);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t("resendVerificationFailed");

      Toast.show(errorMessage, { type: "danger" });
    }
  };

  const handleLanguageChange = async (languageCode: string) => {
    setShowLanguages(false);
    dispatch(appActions.setLocale(languageCode));
  };

  return (
    <View style={styles.container}>
      {/* Left Panel - Branding */}
      <View style={styles.leftPanel}>
        <View style={styles.brandingContent}>
          <Image
            source={require("../../assets/logo-no-bg-white.png")}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={styles.brandTitle}>خدام</Text>
          <Text style={styles.brandSubtitle}>
            {i18n.t("welcomeIntoDesc1")}
          </Text>
          <Image
            source={VerifyImage}
            style={styles.illustrationImage}
            resizeMode="contain"
          />
        </View>
      </View>

      {/* Right Panel - Verification Form */}
      <View style={styles.rightPanel}>
        <View style={styles.formContainer}>
          {/* Language Selector */}
          <View style={styles.languageSelector}>
            <TouchableOpacity
              style={styles.languageButton}
              onPress={() => setShowLanguages(!showLanguages)}
            >
              <Text style={styles.languageButtonText}>🌐</Text>
              <Text style={styles.languageButtonLabel}>
                {availableLocales.find((l) => l.code === locale)?.name ||
                  locale}
              </Text>
            </TouchableOpacity>

            {showLanguages && (
              <View style={styles.languageDropdown}>
                {availableLocales.map((lang) => (
                  <TouchableOpacity
                    key={lang.code}
                    style={[
                      styles.languageOption,
                      locale === lang.code && styles.languageOptionActive,
                    ]}
                    onPress={() => handleLanguageChange(lang.code)}
                  >
                    <Text
                      style={[
                        styles.languageOptionText,
                        locale === lang.code && styles.languageOptionTextActive,
                      ]}
                    >
                      {lang.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          {/* Icon */}
          <View style={styles.iconContainer}>
            <View style={styles.iconCircle}>
              <Feather name="mail" size={48} color="#0596D0" />
            </View>
          </View>

          {/* Header */}
          <View style={styles.formHeader}>
            <Text style={styles.formTitle}>
              {verificationType === "password-reset"
                ? i18n.t("verifyPasswordReset") ||
                  "تأكيد إعادة تعيين كلمة المرور"
                : i18n.t("verifyEmail")}
            </Text>
            <Text style={styles.formSubtitle}>
              {verificationType === "password-reset"
                ? i18n.t("passwordResetVerificationDescription") ||
                  "أدخل رمز التحقق المرسل إلى بريدك الإلكتروني"
                : i18n.t("emailVerificationDescription") ||
                  "أدخل رمز التحقق المرسل إلى بريدك الإلكتروني لتأكيد حسابك"}
            </Text>
          </View>

          {/* Email Display */}
          {userEmail && (
            <View style={styles.emailDisplay}>
              <Text style={styles.emailLabel}>
                {i18n.t("sentTo")}
              </Text>
              <Text style={styles.emailValue}>{userEmail}</Text>
            </View>
          )}

          {/* Verification Code Field */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>
              {i18n.t("enterVerificationCode")}
            </Text>
            <View style={styles.inputWrapper}>
              <Feather
                name="key"
                size={20}
                color="#999"
                style={styles.inputIcon}
              />
              <TextInput
                style={styles.textInput}
                placeholder="123456"
                placeholderTextColor="#999"
                value={verificationCode}
                onChangeText={setVerificationCode}
                keyboardType="number-pad"
                maxLength={6}
              />
            </View>
          </View>

          {/* Verify Button */}
          <TouchableOpacity
            style={styles.verifyButton}
            onPress={handleVerifyEmail}
            disabled={buttonSpinner}
          >
            {buttonSpinner ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.verifyButtonText}>
                {i18n.t("verifyEmail")}
              </Text>
            )}
          </TouchableOpacity>

          {/* Resend Code */}
          <View style={styles.resendContainer}>
            <Text style={styles.resendText}>
              {i18n.t("didntReceiveCode")}
            </Text>
            <TouchableOpacity
              onPress={handleResendCode}
              disabled={resendSpinner}
            >
              {resendSpinner ? (
                <ActivityIndicator size="small" color="#0596D0" />
              ) : (
                <Text style={styles.resendLink}>
                  {i18n.t("resendCode")}
                </Text>
              )}
            </TouchableOpacity>
          </View>

          {/* Back to Login */}
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => router.push("/(routes)/(auth)/login")}
          >
            <Text style={styles.backButtonText}>
              {i18n.t("backToLogin")}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
    minHeight: "100vh" as any,
    backgroundColor: "#F8F9FA",
  },
  leftPanel: {
    flex: 1,
    backgroundColor: "#004E6D",
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  brandingContent: {
    alignItems: "center",
    maxWidth: 400,
  },
  logo: {
    width: 80,
    height: 80,
    marginBottom: 24,
  },
  brandTitle: {
    fontSize: 56,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginBottom: 16,
  },
  brandSubtitle: {
    fontSize: 20,
    color: "rgba(255, 255, 255, 0.9)",
    textAlign: "center",
    lineHeight: 28,
    marginBottom: 40,
  },
  illustrationImage: {
    width: 280,
    height: 220,
  },
  rightPanel: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
    backgroundColor: "#FFFFFF",
  },
  formContainer: {
    width: "100%",
    maxWidth: 420,
  },
  languageSelector: {
    position: "absolute" as any,
    top: -60,
    right: 0,
    zIndex: 100,
  },
  languageButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F0F0F0",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 8,
  },
  languageButtonText: {
    fontSize: 20,
  },
  languageButtonLabel: {
    fontSize: 16,
    color: "#333",
    fontWeight: "500",
  },
  languageDropdown: {
    position: "absolute" as any,
    top: 44,
    right: 0,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 5,
    minWidth: 140,
    overflow: "hidden",
    zIndex: 9999,
  },
  languageOption: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  languageOptionActive: {
    backgroundColor: "#E8F4F8",
  },
  languageOptionText: {
    fontSize: 16,
    color: "#333",
  },
  languageOptionTextActive: {
    color: "#004E6D",
    fontWeight: "600",
  },
  iconContainer: {
    alignItems: "center",
    marginBottom: 24,
  },
  iconCircle: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "#E3F2FD",
    justifyContent: "center",
    alignItems: "center",
  },
  formHeader: {
    marginBottom: 32,
  },
  formTitle: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#004E6D",
    marginBottom: 12,
    textAlign: "center",
  },
  formSubtitle: {
    fontSize: 18,
    color: "#666",
    textAlign: "center",
    lineHeight: 24,
  },
  emailDisplay: {
    backgroundColor: "#F8F9FA",
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    alignItems: "center",
  },
  emailLabel: {
    fontSize: 14,
    color: "#666",
    marginBottom: 4,
  },
  emailValue: {
    fontSize: 18,
    color: "#004E6D",
    fontWeight: "600",
  },
  inputGroup: {
    marginBottom: 24,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: "500",
    color: "#333",
    marginBottom: 8,
    textAlign: "right",
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8F9FA",
    borderWidth: 1,
    borderColor: "#E0E0E0",
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 56,
  },
  inputIcon: {
    marginRight: 12,
  },
  textInput: {
    flex: 1,
    fontSize: 22,
    color: "#333",
    textAlign: "center",
    letterSpacing: 8,
    outlineStyle: "none" as any,
  },
  verifyButton: {
    backgroundColor: "#004E6D",
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: "center",
    marginBottom: 20,
    shadowColor: "#004E6D",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  verifyButtonText: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "600",
  },
  resendContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    marginBottom: 24,
  },
  resendText: {
    fontSize: 16,
    color: "#666",
  },
  resendLink: {
    fontSize: 16,
    color: "#0596D0",
    fontWeight: "600",
  },
  backButton: {
    alignItems: "center",
  },
  backButtonText: {
    fontSize: 16,
    color: "#666",
  },
});

export default WebVerifyEmailScreen;
